import React, { useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Plane, Car, Mountain, FileText, Hotel, Palmtree, ArrowRight, Star, Phone } from 'lucide-react';
import CircularPackageSlider from '../components/CircularPackageSlider';
import { domesticPackages, internationalPackages, services, testimonials } from '../mock';

const Home = () => {
  const videoRef = useRef(null);

  useEffect(() => {
    if (videoRef.current) {
      videoRef.current.play().catch(err => console.log('Video autoplay prevented'));
    }
  }, []);

  const iconMap = {
    Palmtree,
    Plane,
    Car,
    Mountain,
    FileText,
    Hotel
  };

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative h-screen flex items-center justify-center overflow-hidden">
        {/* Background Video */}
        <video
          ref={videoRef}
          className="absolute inset-0 w-full h-full object-cover"
          loop
          muted
          playsInline
          autoPlay
        >
          <source src="https://customer-assets.emergentagent.com/job_1c7269e2-1feb-426d-b007-6b7d8cae6132/artifacts/rrn07dsy_Ladakh%202024%20video.mp4" type="video/mp4" />
        </video>
        
        {/* Overlay */}
        <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-black/40 to-black/60"></div>
        
        {/* Content */}
        <div className="relative z-10 container mx-auto px-4 text-center text-white">
          <h1 className="text-5xl md:text-7xl font-bold mb-6 animate-in fade-in slide-in-from-bottom duration-1000">
            Your Journey,<br />
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-red-400 to-blue-400">
              To Ur Manzil
            </span>
          </h1>
          <p className="text-xl md:text-2xl mb-8 max-w-3xl mx-auto animate-in fade-in slide-in-from-bottom duration-1000 delay-200">
            Experience the world with TUM Holidays - Your trusted partner for unforgettable domestic and international adventures
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center animate-in fade-in slide-in-from-bottom duration-1000 delay-400">
            <Button 
              asChild
              size="lg"
              className="bg-gradient-to-r from-red-600 to-blue-600 hover:from-red-700 hover:to-blue-700 text-white text-lg px-8 py-6 transition-all duration-300 hover:scale-105"
            >
              <Link to="/domestic">Explore Packages</Link>
            </Button>
            <Button 
              asChild
              size="lg"
              variant="outline"
              className="bg-white/10 backdrop-blur-sm border-2 border-white text-white hover:bg-white hover:text-gray-900 text-lg px-8 py-6 transition-all duration-300 hover:scale-105"
            >
              <Link to="/contact">Contact Us</Link>
            </Button>
          </div>
        </div>

        {/* Scroll Indicator */}
        <div className="absolute bottom-8 left-1/2 -translate-x-1/2 animate-bounce">
          <div className="w-6 h-10 border-2 border-white rounded-full flex items-start justify-center p-2">
            <div className="w-1 h-3 bg-white rounded-full animate-pulse"></div>
          </div>
        </div>
      </section>

      {/* Special Backpacking Trips Section */}
      <section className="py-20 bg-gradient-to-br from-red-50 to-blue-50">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-4xl md:text-5xl font-bold mb-4 text-gray-900">
              Weekend Adventures
            </h2>
            <p className="text-xl text-gray-600">
              Join our <span className="text-red-600 font-semibold">Spiti Valley & Leh Ladakh</span> backpacking trips every Saturday!
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8 max-w-5xl mx-auto">
            {domesticPackages.filter(pkg => pkg.type === 'backpacking').map((pkg) => (
              <div key={pkg.id} className="group relative bg-white rounded-3xl overflow-hidden shadow-xl hover:shadow-2xl transition-all duration-500">
                <div className="relative h-80 overflow-hidden">
                  <img 
                    src={pkg.image} 
                    alt={pkg.name}
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent"></div>
                  <div className="absolute top-4 right-4 bg-red-600 text-white px-4 py-2 rounded-full text-sm font-semibold flex items-center gap-2">
                    <span className="w-2 h-2 bg-white rounded-full animate-pulse"></span>
                    Every Saturday
                  </div>
                  <div className="absolute bottom-0 left-0 right-0 p-6">
                    <h3 className="text-3xl font-bold text-white mb-2">{pkg.name}</h3>
                    <p className="text-white/90 mb-4">{pkg.duration} | {pkg.departure}</p>
                    <div className="flex items-center justify-between">
                      <div className="text-white">
                        <span className="text-3xl font-bold">₹{pkg.price.toLocaleString()}</span>
                        <span className="text-sm">/person</span>
                      </div>
                      <Button 
                        asChild
                        className="bg-white text-gray-900 hover:bg-gray-100 transition-all duration-300 hover:scale-105"
                      >
                        <Link to="/contact">Book Now <ArrowRight className="ml-2" size={18} /></Link>
                      </Button>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Featured Domestic Packages */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-4">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
              Explore India
            </h2>
            <p className="text-xl text-gray-600 mb-8">Discover the beauty and diversity of incredible India</p>
          </div>
          <CircularPackageSlider packages={domesticPackages.slice(0, 6)} />
          <div className="text-center mt-8">
            <Button 
              asChild
              size="lg"
              variant="outline"
              className="border-2 border-red-600 text-red-600 hover:bg-red-600 hover:text-white transition-all duration-300"
            >
              <Link to="/domestic">View All Domestic Packages <ArrowRight className="ml-2" size={18} /></Link>
            </Button>
          </div>
        </div>
      </section>

      {/* Featured International Packages */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="text-center mb-4">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
              International Escapes
            </h2>
            <p className="text-xl text-gray-600 mb-8">Embark on extraordinary journeys around the globe</p>
          </div>
          <CircularPackageSlider packages={internationalPackages.slice(0, 6)} />
          <div className="text-center mt-8">
            <Button 
              asChild
              size="lg"
              variant="outline"
              className="border-2 border-blue-600 text-blue-600 hover:bg-blue-600 hover:text-white transition-all duration-300"
            >
              <Link to="/international">View All International Packages <ArrowRight className="ml-2" size={18} /></Link>
            </Button>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
              Our Services
            </h2>
            <p className="text-xl text-gray-600">Everything you need for the perfect journey</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {services.map((service) => {
              const Icon = iconMap[service.icon];
              return (
                <div 
                  key={service.id}
                  className="group bg-white border-2 border-gray-100 rounded-2xl p-8 hover:border-red-200 hover:shadow-xl transition-all duration-300 hover:scale-105"
                >
                  <div className="w-16 h-16 bg-gradient-to-br from-red-100 to-blue-100 rounded-2xl flex items-center justify-center mb-4 group-hover:scale-110 transition-transform duration-300">
                    <Icon size={32} className="text-red-600" />
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-2">{service.title}</h3>
                  <p className="text-gray-600">{service.description}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-20 bg-gradient-to-br from-blue-50 to-red-50">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
              What Our Travelers Say
            </h2>
            <p className="text-xl text-gray-600">Real experiences from real travelers</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-6xl mx-auto">
            {testimonials.map((testimonial) => (
              <div 
                key={testimonial.id}
                className="bg-white rounded-2xl p-8 shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-105"
              >
                <div className="flex items-center gap-4 mb-4">
                  <img 
                    src={testimonial.image} 
                    alt={testimonial.name}
                    className="w-16 h-16 rounded-full object-cover"
                  />
                  <div>
                    <h4 className="font-bold text-gray-900">{testimonial.name}</h4>
                    <p className="text-sm text-gray-600">{testimonial.location}</p>
                  </div>
                </div>
                <div className="flex gap-1 mb-4">
                  {[...Array(testimonial.rating)].map((_, i) => (
                    <Star key={i} size={18} className="fill-yellow-400 text-yellow-400" />
                  ))}
                </div>
                <p className="text-gray-700 italic">"{testimonial.comment}"</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-red-600 to-blue-600 text-white">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-4xl md:text-5xl font-bold mb-6">
            Ready to Start Your Journey?
          </h2>
          <p className="text-xl mb-8 max-w-2xl mx-auto">
            Let us help you create unforgettable memories. Contact us today to plan your perfect trip!
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button 
              asChild
              size="lg"
              className="bg-white text-red-600 hover:bg-gray-100 text-lg px-8 py-6 transition-all duration-300 hover:scale-105"
            >
              <Link to="/contact">Book Your Trip Now</Link>
            </Button>
            <Button 
              asChild
              size="lg"
              variant="outline"
              className="border-2 border-white text-white hover:bg-white hover:text-red-600 text-lg px-8 py-6 transition-all duration-300 hover:scale-105"
            >
              <a href="tel:+918283950765">
                <Phone className="mr-2" size={20} />
                Call Us Now
              </a>
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;